import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Search, X, Loader2, User } from 'lucide-react';
import { useUserSelector } from './useUserSelector';
import { useUser } from '../../../context/UserContext';

const INPUT_BASE_CLASS =
  'w-full rounded-lg px-2 py-1.5 text-xs text-slate-700 border border-gray-300 bg-surface-muted shadow-sm focus:border-primary-dark focus:outline-none focus:ring-1 focus:ring-primary-dark/40 transition disabled:cursor-not-allowed disabled:opacity-70 disabled:bg-gray-100';

/**
 * Get display name for a user
 * @param {Object} user - User object
 * @returns {string}
 */
function getUserDisplayName(user) {
  if (!user) return '';
  if (user.fullName) return user.fullName;
  const names = [user.firstName, user.lastName].filter(Boolean);
  return names.length > 0 ? names.join(' ').trim() : user.email || '';
}

/**
 * Resolves display text from value. Shows user name or email.
 * @param {Object|string} value - { user, displayText, ... } or string
 * @returns {string}
 */
function getDisplayValue(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value.user) {
    return getUserDisplayName(value.user);
  }
  if (typeof value === 'object' && value.displayText) return value.displayText;
  if (typeof value === 'object' && value.fullName) return value.fullName;
  if (typeof value === 'object') {
    const names = [value.firstName, value.lastName].filter(Boolean);
    return names.length > 0 ? names.join(' ').trim() : value.email || '';
  }
  return '';
}

/**
 * User selector: label, read-only field showing selected user, and Select button.
 * Clicking Select opens a search dialog. Defaults to currently logged-in user.
 * Search is debounced by 2 seconds.
 *
 * @param {Object} props
 * @param {string} props.id - Input id
 * @param {string} [props.label] - Label
 * @param {boolean} [props.required]
 * @param {string} [props.error]
 * @param {Object|string} props.value - Current selection: { user, displayText, ... } or string
 * @param {function(Object|null): void} props.onChange - Called with { user, displayText } or null
 * @param {string} [props.placeholder] - Placeholder when nothing selected
 * @param {number} [props.labelWidthRem]
 * @param {'top'|'left'} [props.labelPosition]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.defaultToCurrentUser=true] - Whether to default to logged-in user
 * @param {number} [props.debounceMs=2000] - Debounce delay in milliseconds
 * @param {'default'|'compact'} [props.variant='default'] - 'compact' for a sleeker inline look (e.g. in chat)
 */
export function UserSelector(props) {
  const {
    id,
    label,
    required = false,
    error,
    value = null,
    onChange,
    placeholder = 'Select user...',
    labelWidthRem = 8,
    labelPosition = 'top',
    disabled = false,
    defaultToCurrentUser = true,
    debounceMs = 2000,
    variant = 'default',
  } = props;

  const { user: currentUser } = useUser();
  const [dialogOpen, setDialogOpen] = useState(false);
  const wrapperRef = useRef(null);
  const hasSetDefaultRef = useRef(false);
  const {
    users,
    loading,
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
  } = useUserSelector(debounceMs);

  // Set default to current user once when currentUser is available and no value is set
  useEffect(() => {
    if (defaultToCurrentUser && !hasSetDefaultRef.current && !value && currentUser && onChange) {
      const displayText = getUserDisplayName(currentUser);
      onChange({
        user: currentUser,
        displayText,
      });
      hasSetDefaultRef.current = true;
    }
  }, [defaultToCurrentUser, currentUser, value, onChange]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!dialogOpen) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setDialogOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dialogOpen, setSearchTerm]);

  const displayValue = getDisplayValue(value);
  const errorId = id ? `${id}-error` : undefined;
  const isTop = labelPosition === 'top';
  const isCompact = variant === 'compact';

  const handleSelectUser = (user) => {
    const displayText = getUserDisplayName(user);
    onChange?.({
      user,
      displayText,
    });
    setDialogOpen(false);
    setSearchTerm('');
  };

  const handleClear = () => {
    onChange?.(null);
    setSearchTerm('');
  };

  const showSuggestions = dialogOpen && (debouncedSearchTerm.trim().length >= 2 || users.length > 0);
  const filteredUsers = useMemo(() => {
    if (!showSuggestions) return [];
    // If search term is less than 2 chars, show current user if available
    if (debouncedSearchTerm.trim().length < 2) {
      return currentUser ? [currentUser] : [];
    }
    return users;
  }, [showSuggestions, debouncedSearchTerm, users, currentUser]);

  const labelEl = label ? (
    <label htmlFor={id} className="text-sm font-medium text-primary-dark">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
  ) : null;

  const containerClass = isCompact
    ? 'flex gap-0 rounded-xl border border-cream/60 bg-white/80 overflow-hidden shadow-sm transition hover:border-primary/30 focus-within:border-primary/50 focus-within:ring-1 focus-within:ring-primary/20'
    : 'flex gap-0 rounded border border-gray-300 bg-white overflow-hidden';
  const displayClass = isCompact
    ? 'flex-1 min-w-0 flex items-center px-3 py-2'
    : 'flex-1 min-w-0 flex items-center px-2 py-1.5';
  const clearBtnClass = isCompact
    ? 'px-2.5 py-2 text-xs text-slate-500 hover:text-red-600 hover:bg-red-50/80 border-l border-cream/60 transition focus:outline-none focus:ring-1 focus:ring-red-500/30 disabled:opacity-60 disabled:cursor-not-allowed'
    : 'px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 border-l border-gray-300 focus:outline-none focus:ring-1 focus:ring-red-500/40 disabled:opacity-60 disabled:cursor-not-allowed';
  const selectBtnClass = isCompact
    ? 'h-full px-3 py-2 text-xs font-medium text-white bg-primary hover:bg-primary/90 border-l border-primary/30 transition focus:outline-none focus:ring-1 focus:ring-primary/40 focus:ring-inset disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1.5'
    : 'h-full px-3 py-1.5 text-xs font-medium text-slate-700 bg-gray-100 border-l border-gray-300 hover:bg-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-dark/40 focus:ring-inset disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1.5';

  const fieldContent = (
    <div className="min-w-0 relative" ref={wrapperRef}>
      <div className={containerClass}>
        <div className={displayClass}>
          {displayValue ? (
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <User className={`w-4 h-4 shrink-0 ${isCompact ? 'text-primary/70' : 'text-slate-500'}`} />
              <span className="text-xs text-slate-700 truncate">{displayValue}</span>
            </div>
          ) : (
            <span className={`text-xs ${isCompact ? 'text-slate-500' : 'text-slate-400'}`}>{placeholder}</span>
          )}
        </div>
        {displayValue && (
          <button
            type="button"
            onClick={handleClear}
            disabled={disabled}
            className={clearBtnClass}
            aria-label="Clear selection"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={() => !disabled && setDialogOpen(!dialogOpen)}
            disabled={disabled}
            className={selectBtnClass}
            aria-haspopup="true"
            aria-expanded={dialogOpen}
            aria-label="Select user"
          >
            <Search className="w-3.5 h-3.5" />
            <span>Select</span>
          </button>
        </div>
      </div>

      {/* Search dropdown */}
      {dialogOpen && (
        <div className="absolute z-50 mt-1 w-full min-w-[20rem] rounded-lg border border-gray-200 bg-white shadow-lg max-h-80 overflow-hidden flex flex-col">
          {/* Search input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search users..."
                className="w-full pl-8 pr-2 py-1.5 text-xs text-slate-700 border border-gray-300 rounded focus:border-primary-dark focus:outline-none focus:ring-1 focus:ring-primary-dark/40"
                autoFocus
              />
              {loading && (
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />
                </div>
              )}
            </div>
            {debouncedSearchTerm.trim().length > 0 && debouncedSearchTerm.trim().length < 2 && (
              <p className="mt-1 text-xs text-slate-500">Type at least 2 characters to search</p>
            )}
          </div>

          {/* Results list */}
          <div className="flex-1 overflow-y-auto">
            {loading && debouncedSearchTerm.trim().length >= 2 ? (
              <div className="px-3 py-4 text-xs text-slate-500 flex items-center justify-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Searching...
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="px-3 py-4 text-xs text-slate-500 text-center">
                {debouncedSearchTerm.trim().length >= 2
                  ? 'No users found'
                  : 'Type at least 2 characters to search'}
              </div>
            ) : (
              <div role="listbox" className="py-1">
                {filteredUsers.map((user) => {
                  const name = getUserDisplayName(user);
                  const isSelected = value?.user?._id === user._id || value?.user?.id === user.id;
                  return (
                    <button
                      key={user._id || user.id}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => handleSelectUser(user)}
                      className={`w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none flex items-center gap-2 ${
                        isSelected ? 'bg-primary/10' : ''
                      }`}
                    >
                      <User className="w-4 h-4 text-slate-400 shrink-0" />
                      <span className="flex-1 min-w-0">
                        <span className="block truncate">{name}</span>
                        {user.email && (
                          <span className="block text-xs text-slate-500 truncate">{user.email}</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {error && (
        <p id={errorId} className="mt-1 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );

  return (
    <>
      {isTop ? (
        <div className="space-y-1">
          {labelEl}
          {fieldContent}
        </div>
      ) : (
        <div
          className="grid items-center gap-y-1"
          style={{ gridTemplateColumns: `${labelWidthRem}rem 1fr` }}
        >
          {labelEl ?? <div />}
          {fieldContent}
        </div>
      )}
    </>
  );
}

export default UserSelector;
